"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  ArrowRight,
  ChevronDown,
  Loader2,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  SkipBack,
  SkipForward,
  X,
} from "lucide-react";
import * as FocusScope from "@radix-ui/react-focus-scope";
import { Drawer as DrawerPrimitive } from "vaul";

import { formatDuration } from "@/lib/podcast";
import {
  setMiniDrawerOpen,
  useMiniDrawerOpen,
} from "@/lib/podcast-mini-drawer";
import { isLpPath, regionFromPathname, regionPath } from "@/lib/region";

import { PodcastLogoTile } from "./podcast-logo-tile";
import {
  usePlayerActions,
  usePlayerLibrary,
  usePlayerState,
} from "./podcast-player";

const PODCAST_TITLE = "Fertility Talks";

function buildEyebrow(
  seasonNumber: number | null,
  episodeNumber: number | null,
): string {
  const parts: string[] = [PODCAST_TITLE];
  if (seasonNumber != null) parts.push(`S${seasonNumber}`);
  if (episodeNumber != null) parts.push(`Ep ${episodeNumber}`);
  return parts.join(" · ");
}

function isPodcastPath(pathname: string): boolean {
  const trimmed = pathname.replace(/\/$/, "");
  return trimmed === "/podcast" || trimmed === "/uk/podcast";
}

export function MiniPlayer() {
  const pathname = usePathname();
  const state = usePlayerState();
  const actions = usePlayerActions();
  const [desktopExpanded, setDesktopExpanded] = useState(false);
  const drawerOpen = useMiniDrawerOpen();

  if (!state.episode) return null;
  if (isPodcastPath(pathname)) return null;
  if (isLpPath(pathname)) return null;

  const episode = state.episode;
  const duration =
    state.duration > 0 ? state.duration : episode.duration ?? 0;
  const pct = duration > 0 ? (state.currentTime / duration) * 100 : 0;
  const playLabel = state.loading
    ? "Loading"
    : state.playing
      ? "Pause"
      : "Play";

  const onTogglePlay = () => actions.toggle(episode, state.seasonNumber);
  const onClose = () => actions.stop();

  return (
    <>
      <div
        role="complementary"
        className="pointer-events-none fixed bottom-8 left-8 z-40 hidden md:block"
        aria-label="Now playing"
      >
        {desktopExpanded ? (
          <DesktopExpanded
            onMinimise={() => setDesktopExpanded(false)}
            onClose={onClose}
          />
        ) : (
          <div className="pointer-events-auto w-[360px] overflow-hidden rounded-2xl bg-white shadow-[0_20px_48px_-12px_rgba(20,10,60,0.25)] ring-1 ring-black/10">
            <div className="flex items-center gap-3 p-3">
              <PodcastLogoTile
                size="sm"
                seasonNumber={state.seasonNumber}
                playing={state.playing}
              />
              <button
                type="button"
                onClick={() => setDesktopExpanded(true)}
                className="min-w-0 flex-1 cursor-pointer text-left"
                aria-label="Expand player"
              >
                <div className="text-purple-03 truncate text-caption leading-tight font-semibold tracking-[0.08em] uppercase">
                  {buildEyebrow(state.seasonNumber, episode.number)}
                </div>
                <div className="text-purple-05 text-button truncate leading-tight font-bold">
                  {episode.title}
                </div>
              </button>
              <PlayPauseButton
                loading={state.loading}
                playing={state.playing}
                onClick={onTogglePlay}
                label={playLabel}
              />
              <button
                type="button"
                onClick={onClose}
                aria-label="Close player"
                className="hover:bg-dusk-02 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-black/50 transition-colors hover:text-black"
              >
                <X className="size-4" />
              </button>
            </div>
            <div className="bg-dusk-02 h-1">
              <div
                className="bg-purple-03 h-full transition-[width]"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {drawerOpen && (
        <MobileDrawer
          onClose={() => setMiniDrawerOpen(false)}
          onDismissPlayer={onClose}
        />
      )}
    </>
  );
}

function PlayPauseButton({
  loading,
  playing,
  onClick,
  label,
  size = "sm",
}: {
  loading: boolean;
  playing: boolean;
  onClick: () => void;
  label: string;
  size?: "sm" | "lg";
}) {
  const sizeClass = size === "lg" ? "size-14" : "size-9";
  const iconClass = size === "lg" ? "size-6" : "size-4";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-busy={loading || undefined}
      className={`bg-purple-03 hover:bg-purple-04 ${sizeClass} flex shrink-0 cursor-pointer items-center justify-center rounded-full text-white transition-colors`}
    >
      {loading ? (
        <Loader2 className={`${iconClass} animate-spin`} />
      ) : playing ? (
        <Pause className={iconClass} />
      ) : (
        <Play className={`ml-0.5 ${iconClass}`} />
      )}
    </button>
  );
}

function TransportRow() {
  const state = usePlayerState();
  const actions = usePlayerActions();
  const library = usePlayerLibrary();
  if (!state.episode) return null;
  const episode = state.episode;
  return (
    <div className="flex items-center justify-center gap-2">
      <button
        type="button"
        onClick={() => actions.seekBy(-15)}
        aria-label="Rewind 15 seconds"
        className="text-purple-03 hover:bg-dusk-02 flex size-10 cursor-pointer items-center justify-center rounded-full transition-colors"
      >
        <RotateCcw className="size-5" />
      </button>
      <button
        type="button"
        onClick={() => actions.playPrev()}
        disabled={!library.hasPrev}
        aria-label="Previous episode"
        className="text-purple-03 hover:bg-dusk-02 flex size-10 cursor-pointer items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-30"
      >
        <SkipBack className="size-5" />
      </button>
      <PlayPauseButton
        loading={state.loading}
        playing={state.playing}
        onClick={() => actions.toggle(episode, state.seasonNumber)}
        label={
          state.loading ? "Loading" : state.playing ? "Pause" : "Play"
        }
        size="lg"
      />
      <button
        type="button"
        onClick={() => actions.playNext()}
        disabled={!library.hasNext}
        aria-label="Next episode"
        className="text-purple-03 hover:bg-dusk-02 flex size-10 cursor-pointer items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-30"
      >
        <SkipForward className="size-5" />
      </button>
      <button
        type="button"
        onClick={() => actions.seekBy(30)}
        aria-label="Forward 30 seconds"
        className="text-purple-03 hover:bg-dusk-02 flex size-10 cursor-pointer items-center justify-center rounded-full transition-colors"
      >
        <RotateCw className="size-5" />
      </button>
    </div>
  );
}

function SeasonChips() {
  const state = usePlayerState();
  const actions = usePlayerActions();
  const library = usePlayerLibrary();
  if (library.seasons.length === 0) return null;
  return (
    <div className="flex items-center justify-center gap-1.5">
      {library.seasons.map((s) => {
        const active = state.seasonNumber === s.number;
        return (
          <button
            key={s.number}
            type="button"
            onClick={() => actions.playSeason(s.number)}
            aria-pressed={active}
            className={`text-caption flex h-8 min-w-[36px] cursor-pointer items-center justify-center rounded-full px-2.5 font-semibold tracking-wide transition-colors ${
              active
                ? "bg-purple-03 text-white"
                : "text-purple-05 hover:bg-dusk-02 bg-dusk-01"
            }`}
          >
            S{s.number}
          </button>
        );
      })}
    </div>
  );
}

function BrowseEpisodesLink({ pathname }: { pathname: string }) {
  const region = regionFromPathname(pathname);
  const href = regionPath("/podcast", region);
  return (
    <Link
      href={href}
      className="text-purple-03 hover:bg-dusk-02 text-button mx-auto flex items-center gap-1.5 rounded-full px-4 py-2 font-semibold transition-colors"
    >
      Browse all episodes
      <ArrowRight className="size-3.5" />
    </Link>
  );
}

function DesktopExpanded({
  onMinimise,
  onClose,
}: {
  onMinimise: () => void;
  onClose: () => void;
}) {
  const pathname = usePathname();
  const state = usePlayerState();
  const actions = usePlayerActions();
  const trackRef = useRef<HTMLDivElement | null>(null);

  if (!state.episode) return null;
  const episode = state.episode;
  const duration =
    state.duration > 0 ? state.duration : episode.duration ?? 0;
  const pct = duration > 0 ? (state.currentTime / duration) * 100 : 0;

  const onTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || duration <= 0) return;
    const ratio = (e.clientX - rect.left) / rect.width;
    actions.seek(Math.max(0, Math.min(1, ratio)) * duration);
  };

  return (
    <FocusScope.Root
      asChild
      trapped
      loop
      onMountAutoFocus={(e) => e.preventDefault()}
      onUnmountAutoFocus={(e) => e.preventDefault()}
    >
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Now playing"
      onKeyDown={(e) => {
        if (e.key === "Escape") onMinimise();
      }}
      className="pointer-events-auto flex w-[440px] flex-col gap-5 rounded-2xl bg-white p-5 shadow-[0_24px_56px_-12px_rgba(20,10,60,0.3)] ring-1 ring-black/10"
      style={{ animation: "mini-player-expand 0.2s ease-out" }}
    >
      <div className="flex items-start gap-4">
        <PodcastLogoTile
          size="md"
          seasonNumber={state.seasonNumber}
          playing={state.playing}
        />
        <div className="min-w-0 flex-1">
          <div className="text-purple-03 text-caption font-semibold tracking-[0.08em] uppercase">
            {buildEyebrow(state.seasonNumber, episode.number)}
          </div>
          <div className="text-purple-05 mt-0.5 text-h6 leading-tight font-bold">
            {episode.title}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={onMinimise}
            aria-label="Minimise player"
            className="hover:bg-dusk-02 flex size-8 cursor-pointer items-center justify-center rounded-full text-black/50 transition-colors hover:text-black"
          >
            <ChevronDown className="size-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close player"
            className="hover:bg-dusk-02 flex size-8 cursor-pointer items-center justify-center rounded-full text-black/50 transition-colors hover:text-black"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div
          ref={trackRef}
          onClick={onTrackClick}
          role="slider"
          tabIndex={0}
          aria-valuemin={0}
          aria-valuemax={Math.max(1, Math.round(duration))}
          aria-valuenow={Math.round(state.currentTime)}
          aria-label="Episode scrubber"
          className="bg-dusk-02 relative h-2 cursor-pointer rounded-full"
        >
          <div
            className="bg-purple-03 absolute inset-y-0 left-0 rounded-full"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex justify-between font-mono text-[12px] tabular-nums text-black/55">
          <span>{formatDuration(state.currentTime)}</span>
          <span>{formatDuration(duration)}</span>
        </div>
      </div>

      <TransportRow />

      <SeasonChips />

      <BrowseEpisodesLink pathname={pathname} />

      <style jsx global>{`
        @keyframes mini-player-expand {
          from {
            opacity: 0;
            transform: translateY(12px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
    </FocusScope.Root>
  );
}

function MobileDrawer({
  onClose,
  onDismissPlayer,
}: {
  onClose: () => void;
  onDismissPlayer: () => void;
}) {
  const pathname = usePathname();
  const state = usePlayerState();
  const actions = usePlayerActions();
  const trackRef = useRef<HTMLDivElement | null>(null);

  if (!state.episode) return null;
  const episode = state.episode;
  const duration =
    state.duration > 0 ? state.duration : episode.duration ?? 0;
  const pct = duration > 0 ? (state.currentTime / duration) * 100 : 0;

  const onTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || duration <= 0) return;
    const ratio = (e.clientX - rect.left) / rect.width;
    actions.seek(Math.max(0, Math.min(1, ratio)) * duration);
  };

  return (
    <DrawerPrimitive.Root open onOpenChange={(v) => !v && onClose()}>
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-[2px]" />
        <DrawerPrimitive.Content className="fixed inset-x-0 bottom-0 z-50 mx-auto flex max-h-[92dvh] max-w-md flex-col rounded-t-[24px] bg-white text-black shadow-[0_-20px_40px_-12px_rgba(0,0,0,0.25)] outline-none">
          <DrawerPrimitive.Title className="sr-only">
            {episode.title}
          </DrawerPrimitive.Title>
          <DrawerPrimitive.Description className="sr-only">
            Podcast player controls
          </DrawerPrimitive.Description>

          <div className="mx-auto mt-3 h-1.5 w-10 shrink-0 rounded-full bg-black/15" />

          <div className="flex items-center justify-between gap-3 px-6 pt-4">
            <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-black/55">
              Now playing
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Minimise player"
              className="hover:bg-dusk-02 flex size-8 cursor-pointer items-center justify-center rounded-full text-black/50 transition-colors hover:text-black"
            >
              <ChevronDown className="size-4" />
            </button>
          </div>

          <div className="flex flex-col gap-5 px-6 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] pt-4">
            <div className="flex items-center gap-4">
              <PodcastLogoTile
                size="md"
                seasonNumber={state.seasonNumber}
                playing={state.playing}
              />
              <div className="min-w-0">
                <div className="text-purple-03 text-[11px] font-semibold tracking-[0.08em] uppercase">
                  {buildEyebrow(state.seasonNumber, episode.number)}
                </div>
                <div className="text-purple-05 mt-0.5 line-clamp-2 text-h6 leading-tight font-bold">
                  {episode.title}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div
                ref={trackRef}
                onClick={onTrackClick}
                role="slider"
                tabIndex={0}
                aria-valuemin={0}
                aria-valuemax={Math.max(1, Math.round(duration))}
                aria-valuenow={Math.round(state.currentTime)}
                aria-label="Episode scrubber"
                className="bg-dusk-02 relative h-2 cursor-pointer rounded-full"
              >
                <div
                  className="bg-purple-03 absolute inset-y-0 left-0 rounded-full"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="flex justify-between font-mono text-[12px] tabular-nums text-black/55">
                <span>{formatDuration(state.currentTime)}</span>
                <span>{formatDuration(duration)}</span>
              </div>
            </div>

            <TransportRow />

            <SeasonChips />

            <div className="flex items-center justify-center gap-1">
              <BrowseEpisodesLink pathname={pathname} />
              <button
                type="button"
                onClick={() => {
                  onDismissPlayer();
                  onClose();
                }}
                className="text-button text-black/55 hover:bg-dusk-02 flex items-center gap-1.5 rounded-full px-4 py-2 font-semibold transition-colors hover:text-black"
              >
                <X className="size-3.5" />
                Stop
              </button>
            </div>
          </div>
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}
